import { useMemo, useState, type ReactNode } from 'react'
import { ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react'

export interface Column<T> {
  key: string
  header: ReactNode
  align?: 'left' | 'right' | 'center'
  width?: string | number
  render?: (row: T) => ReactNode
  sortValue?: (row: T) => string | number
  sortable?: boolean
  className?: string
}

export function DataTable<T>({
  columns, rows, getRowKey, onRowClick, initialSort, dense = false, emptyText = 'No records.',
}: {
  columns: Column<T>[]
  rows: T[]
  getRowKey: (row: T, i: number) => string
  onRowClick?: (row: T) => void
  initialSort?: { key: string; dir: 'asc' | 'desc' }
  dense?: boolean
  emptyText?: string
}) {
  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' } | undefined>(initialSort)

  const sorted = useMemo(() => {
    if (!sort) return rows
    const col = columns.find((c) => c.key === sort.key)
    if (!col?.sortValue) return rows
    const dir = sort.dir === 'asc' ? 1 : -1
    return [...rows].sort((a, b) => {
      const av = col.sortValue!(a), bv = col.sortValue!(b)
      if (av < bv) return -1 * dir
      if (av > bv) return 1 * dir
      return 0
    })
  }, [rows, sort, columns])

  function toggleSort(c: Column<T>) {
    if (!c.sortable && !c.sortValue) return
    setSort((s) =>
      s?.key === c.key
        ? { key: c.key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
        : { key: c.key, dir: 'asc' },
    )
  }

  return (
    <div className="dtable-wrap">
      <table className={`dtable${dense ? ' dtable--dense' : ''}`}>
        <thead>
          <tr>
            {columns.map((c) => {
              const sortable = c.sortable || !!c.sortValue
              return (
                <th
                  key={c.key}
                  className={`align-${c.align ?? 'left'}${sortable ? ' sortable' : ''}`}
                  style={{ width: c.width }}
                  onClick={() => toggleSort(c)}
                >
                  <span className="dth">
                    {c.header}
                    {sortable && (
                      <span className="dth-sort">
                        {sort?.key === c.key
                          ? sort.dir === 'asc' ? <ChevronUp size={13} /> : <ChevronDown size={13} />
                          : <ChevronsUpDown size={13} className="dim" />}
                      </span>
                    )}
                  </span>
                </th>
              )
            })}
          </tr>
        </thead>
        <tbody>
          {sorted.length === 0 && (
            <tr><td className="dtable-empty" colSpan={columns.length}>{emptyText}</td></tr>
          )}
          {sorted.map((row, i) => (
            <tr
              key={getRowKey(row, i)}
              className={onRowClick ? 'row-click' : ''}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
            >
              {columns.map((c) => (
                <td key={c.key} className={`align-${c.align ?? 'left'} ${c.className ?? ''}`}>
                  {c.render ? c.render(row) : (row as Record<string, ReactNode>)[c.key]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
